import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Table, Spinner, Alert, Button,
    ProgressBar, Badge, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function ValeurStock() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('global');

    useEffect(() => {
        fetchValeurStock();
    }, []);

    const fetchValeurStock = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stock_etat.php?action=valeur`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Calcul de la valeur du stock...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Erreur de chargement des données"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchValeurStock}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const valeurMoyenne = (data.global?.valeur_achat || 0) / (data.global?.total_articles || 1);
    const margePotentielle = (data.global?.valeur_vente || 0) - (data.global?.valeur_achat || 0);
    const tauxMarge = ((margePotentielle / (data.global?.valeur_achat || 1)) * 100).toFixed(1);

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-pie-chart-2-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Valeur du stock</h4>
                                    <p className="text-muted small mb-0">
                                        Évaluation financière • {data.global?.total_produits || 0} produits
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant={viewMode === 'global' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('global')}
                                className="rounded-pill px-3"
                                style={viewMode === 'global' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-pie-chart-2-line me-1"></i>Global
                            </Button>
                            <Button 
                                variant={viewMode === 'details' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('details')}
                                className="rounded-pill px-3"
                                style={viewMode === 'details' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-list-check-line me-1"></i>Détails
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stock/etat/instantane')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Cartes principales stylisées */}
                    <Row className="g-4 mb-4">
                        <Col lg={6}>
                            <div className="value-card value-card-primary">
                                <div className="value-card-content">
                                    <div className="value-card-badge">
                                        <i className="ri-shopping-cart-line me-1"></i>
                                        Prix d'achat
                                    </div>
                                    <h3 className="value-card-amount">{formatCurrency(data.global?.valeur_achat || 0)}</h3>
                                    <p className="value-card-footer">Coût total d'acquisition</p>
                                </div>
                                <div className="value-card-icon">
                                    <i className="ri-shopping-cart-line"></i>
                                </div>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div className="value-card value-card-success">
                                <div className="value-card-content">
                                    <div className="value-card-badge">
                                        <i className="ri-money-euro-circle-line me-1"></i>
                                        Prix de vente
                                    </div>
                                    <h3 className="value-card-amount">{formatCurrency(data.global?.valeur_vente || 0)}</h3>
                                    <p className="value-card-footer">Valeur de revente potentielle</p>
                                </div>
                                <div className="value-card-icon">
                                    <i className="ri-money-euro-circle-line"></i>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-stack-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total produits</span>
                                    <h3 className="kpi-value">{data.global?.total_produits || 0}</h3>
                                    <span className="kpi-unit">références actives</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-calculator-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Valeur moyenne</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(valeurMoyenne)}</h3>
                                    <span className="kpi-unit">par article</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={4}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-line-chart-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Marge potentielle</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(margePotentielle)}</h3>
                                    <span className="kpi-unit">taux {tauxMarge}%</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Vue par catégorie */}
                    {viewMode === 'global' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-small">
                                            <i className="ri-folder-chart-line text-primary"></i>
                                        </div>
                                        <h6 className="mb-0 fw-semibold">Répartition par catégorie</h6>
                                    </div>
                                    <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                        {data.par_categorie?.length || 0} catégories
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Catégorie</th>
                                                <th className="text-center">Produits</th>
                                                <th className="text-center">Articles</th>
                                                <th className="text-end">Valeur achat</th>
                                                <th className="text-end">Valeur vente</th>
                                                <th className="pe-4" style={{ width: '200px' }}>Répartition</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.par_categorie?.map((c, index) => {
                                                const pourcentage = (c.valeur_achat / data.global?.valeur_achat) * 100;
                                                return (
                                                    <tr key={index} className="category-row">
                                                        <td className="ps-4">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="avatar-icon-small">
                                                                    <i className="ri-folder-line"></i>
                                                                </div>
                                                                <span className="fw-semibold">{c.nom_categorie || 'Sans catégorie'}</span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge className="rounded-pill px-3 " style={{ background: '#e2f3f5', color: '#fff' }}>
                                                                {c.nb_produits}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="fw-semibold">{c.total_articles}</span>
                                                        </td>
                                                        <td className="text-end fw-bold text-primary">{formatCurrency(c.valeur_achat)}</td>
                                                        <td className="text-end text-success">{formatCurrency(c.valeur_vente)}</td>
                                                        <td className="pe-4">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="progress-bar-custom" style={{ width: '120px' }}>
                                                                    <div className="progress-fill" style={{ width: `${pourcentage}%`, background: '#21ACCD' }}></div>
                                                                </div>
                                                                <small className="text-muted">{pourcentage.toFixed(1)}%</small>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue détails avec top produits */}
                    {viewMode === 'details' && (
                        <>
                            <Card className="border-0 shadow-sm rounded-3 overflow-hidden mb-4">
                                <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="section-icon-small">
                                                <i className="ri-trophy-line text-warning"></i>
                                            </div>
                                            <h6 className="mb-0 fw-semibold">Top 10 produits par valeur</h6>
                                        </div>
                                        <Badge className="px-3 py-2 rounded-pill" style={{ background: '#f59e0b' }}>
                                            Plus haute valeur
                                        </Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 custom-table">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-4">Réf.</th>
                                                    <th>Produit</th>
                                                    <th className="text-center">Qté</th>
                                                    <th className="text-end">Prix achat</th>
                                                    <th className="text-end">Prix vente</th>
                                                    <th className="text-end">Valeur achat</th>
                                                    <th className="text-end">Valeur vente</th>
                                                    <th className="text-center pe-4">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.top_produits?.map((p, index) => (
                                                    <tr key={index} className="product-row">
                                                        <td className="ps-4">
                                                            <span className="reference-badge">{p.reference}</span>
                                                        </td>
                                                        <td>
                                                            <Link to={`/stock/produits/${p.id_produit}`} className="table-link">
                                                                {p.designation}
                                                            </Link>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className="quantity-badge">{p.quantite_reelle}</span>
                                                        </td>
                                                        <td className="text-end text-primary">{formatCurrency(p.prix_achat_ht)}</td>
                                                        <td className="text-end text-success">{formatCurrency(p.prix_vente_ht)}</td>
                                                        <td className="text-end fw-bold text-primary">{formatCurrency(p.valeur_achat)}</td>
                                                        <td className="text-end text-success">{formatCurrency(p.valeur_vente)}</td>
                                                        <td className="text-center pe-4">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir le produit</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm"
                                                                    className="action-btn"
                                                                    onClick={() => navigate(`/stock/produits/${p.id_produit}`)}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Graphique de répartition */}
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-small">
                                            <i className="ri-pie-chart-2-line text-primary"></i>
                                        </div>
                                        <h6 className="mb-0 fw-semibold">Répartition de la valeur par catégorie</h6>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-4">
                                        {data.par_categorie?.slice(0, 5).map((c, index) => {
                                            const pourcentage = (c.valeur_achat / data.global?.valeur_achat) * 100;
                                            const colors = ['#21ACCD', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
                                            return (
                                                <Col md={6} key={index}>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="text-muted small">{c.nom_categorie || 'Sans catégorie'}</span>
                                                            <span className="fw-semibold">{formatCurrency(c.valeur_achat)}</span>
                                                        </div>
                                                        <div className="progress-bar-custom" style={{ width: '100%' }}>
                                                            <div className="progress-fill" style={{ width: `${pourcentage}%`, background: colors[index % colors.length] }}></div>
                                                        </div>
                                                        <small className="text-muted float-end mt-1">{pourcentage.toFixed(1)}%</small>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* Value Cards */
                .value-card {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 20px;
                    padding: 1.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    transition: all 0.3s ease;
                    height: 100%;
                }
                .value-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
                }
                .value-card-primary {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                }
                .value-card-success {
                    background: linear-gradient(135deg, #51B048 0%, #8AC983 100%);
                }
                .value-card-content {
                    flex: 1;
                }
                .value-card-badge {
                    display: inline-block;
                    background: rgba(255,255,255,0.25);
                    padding: 0.3rem 1rem;
                    border-radius: 20px;
                    font-size: 0.7rem;
                    font-weight: 500;
                    color: white;
                    margin-bottom: 1rem;
                }
                .value-card-amount {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: white;
                    margin: 0 0 0.5rem 0;
                }
                .value-card-footer {
                    font-size: 0.7rem;
                    color: rgba(255,255,255,0.7);
                    margin: 0;
                }
                .value-card-icon {
                    width: 50px;
                    height: 50px;
                    background: rgba(255,255,255,0.25);
                    border-radius: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .value-card-icon i {
                    font-size: 1.5rem;
                    color: white;
                }
                
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-info { border-left-color: #0ea5e9; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Avatar Icon Small */
                .avatar-icon-small {
                    width: 32px;
                    height: 32px;
                    background: #e2f3f5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                .quantity-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .category-row, .product-row {
                    transition: all 0.2s ease;
                }
                
                .category-row:hover, .product-row:hover {
                    background: #f8fafc;
                }
                
                /* Progress Bar */
                .progress-bar-custom {
                    background: #e2e8f0;
                    border-radius: 4px;
                    height: 6px;
                    overflow: hidden;
                }
                .progress-fill {
                    height: 100%;
                    border-radius: 4px;
                    transition: width 0.3s ease;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}